import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import {
  InventoryMovementDto,
  InventoryReason,
  InventorySource,
  useInventoryMovements,
} from '@shared/api/hooks/useInventory';

const REASON_LABELS: Record<InventoryReason, string> = {
  CHECKOUT_RESERVE: 'Riserva checkout',
  CHECKOUT_RELEASE: 'Rilascio checkout',
  POS_SALE: 'Vendita POS',
  RESTOCK: 'Restock',
  MANUAL_ADJUST: 'Manuale',
  REFUND_RESTOCK: 'Restock rimborso',
};

const REASON_COLORS: Record<InventoryReason, string> = {
  CHECKOUT_RESERVE: 'gold',
  CHECKOUT_RELEASE: 'green',
  POS_SALE: 'magenta',
  RESTOCK: 'green',
  MANUAL_ADJUST: 'blue',
  REFUND_RESTOCK: 'cyan',
};

const SOURCE_LABELS: Record<InventorySource, string> = {
  APP: 'App mobile',
  IN_STORE: 'In store',
  BO: 'Backoffice',
  SYNC_REGISTRATORE: 'Registratore',
};

const SOURCE_COLORS: Record<InventorySource, string> = {
  APP: 'blue',
  IN_STORE: 'cyan',
  BO: 'purple',
  SYNC_REGISTRATORE: 'volcano',
};

type RangeValue = [Dayjs | null, Dayjs | null] | null;

function downloadCsv(rows: InventoryMovementDto[]): void {
  const header = [
    'createdAt',
    'source',
    'reason',
    'delta',
    'productSizeId',
    'refOrderId',
    'refExternalId',
    'notes',
  ];
  const csv = [
    header.join(','),
    ...rows.map((r) =>
      [
        r.createdAt,
        r.source,
        r.reason,
        r.delta,
        r.productSizeId,
        r.refOrderId ?? '',
        r.refExternalId ?? '',
        (r.notes ?? '').replaceAll('"', '""'),
      ]
        .map((v) => (typeof v === 'string' && v.includes(',') ? `"${v}"` : v))
        .join(','),
    ),
  ].join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `inventory-${dayjs().format('YYYYMMDD-HHmm')}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function InventoryPage(): React.ReactElement {
  const [page, setPage] = useState(1);
  const [source, setSource] = useState<InventorySource | undefined>();
  const [reason, setReason] = useState<InventoryReason | undefined>();
  const [range, setRange] = useState<RangeValue>(null);

  const filters = useMemo(
    () => ({
      page,
      limit: 50,
      source,
      reason,
      from: range?.[0]?.startOf('day').toISOString(),
      to: range?.[1]?.endOf('day').toISOString(),
    }),
    [page, source, reason, range],
  );

  const { data, isLoading, refetch, isFetching } = useInventoryMovements(filters);
  const rows = useMemo(() => data?.data ?? [], [data]);
  const meta = data?.meta;

  const oversellCount = useMemo(
    () => rows.filter((r) => r.delta === 0 && r.notes?.startsWith('OVERSELL_BLOCKED')).length,
    [rows],
  );

  return (
    <Card
      title={
        <Space>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Magazzino · audit log
          </Typography.Title>
          <Tag>{meta?.total ?? rows.length}</Tag>
        </Space>
      }
      extra={
        <Space>
          <Tooltip title="Esporta CSV (pagina corrente)">
            <Button
              icon={<DownloadOutlined />}
              onClick={() => downloadCsv(rows)}
              disabled={rows.length === 0}
            >
              CSV
            </Button>
          </Tooltip>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => refetch()}
            loading={isFetching}
          >
            Aggiorna
          </Button>
        </Space>
      }
    >
      {oversellCount > 0 && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message={`${oversellCount} tentativi di vendita bloccati per stock insufficiente nella pagina corrente`}
          description="Verifica l'allineamento tra registratore e backend."
        />
      )}

      <Space style={{ marginBottom: 12 }} wrap>
        <Select
          placeholder="Sorgente"
          allowClear
          style={{ width: 180 }}
          value={source}
          onChange={(v) => {
            setSource(v as InventorySource | undefined);
            setPage(1);
          }}
          options={(Object.keys(SOURCE_LABELS) as InventorySource[]).map((s) => ({
            value: s,
            label: SOURCE_LABELS[s],
          }))}
        />
        <Select
          placeholder="Motivo"
          allowClear
          style={{ width: 180 }}
          value={reason}
          onChange={(v) => {
            setReason(v as InventoryReason | undefined);
            setPage(1);
          }}
          options={(Object.keys(REASON_LABELS) as InventoryReason[]).map((s) => ({
            value: s,
            label: REASON_LABELS[s],
          }))}
        />
        <DatePicker.RangePicker
          value={range}
          format="DD/MM/YYYY"
          onChange={(v) => {
            setRange(v as RangeValue);
            setPage(1);
          }}
          allowEmpty={[true, true]}
        />
      </Space>

      <Table<InventoryMovementDto>
        rowKey="id"
        loading={isLoading}
        dataSource={rows}
        size="small"
        scroll={{ x: 'max-content' }}
        pagination={
          meta
            ? {
                current: meta.page,
                pageSize: meta.limit,
                total: meta.total,
                onChange: (p) => setPage(p),
                showSizeChanger: false,
              }
            : false
        }
        columns={[
          {
            title: 'Quando',
            dataIndex: 'createdAt',
            width: 150,
            render: (v: string) => dayjs(v).format('DD/MM HH:mm:ss'),
          },
          {
            title: 'Sorgente',
            dataIndex: 'source',
            width: 130,
            render: (s: InventorySource) => (
              <Tag color={SOURCE_COLORS[s]}>{SOURCE_LABELS[s]}</Tag>
            ),
          },
          {
            title: 'Motivo',
            dataIndex: 'reason',
            width: 160,
            render: (r: InventoryReason) => (
              <Tag color={REASON_COLORS[r]}>{REASON_LABELS[r]}</Tag>
            ),
          },
          {
            title: 'Δ',
            dataIndex: 'delta',
            width: 80,
            align: 'right',
            render: (v: number, row) => {
              if (v === 0 && row.notes?.startsWith('OVERSELL_BLOCKED')) {
                return <Tag color="red">BLOCCATO</Tag>;
              }
              return (
                <Typography.Text strong style={{ color: v < 0 ? '#cf1322' : '#0a8f3f' }}>
                  {v > 0 ? `+${v}` : v}
                </Typography.Text>
              );
            },
          },
          {
            title: 'Product Size',
            dataIndex: 'productSizeId',
            ellipsis: true,
            render: (v: string) => (
              <Typography.Text code copyable={{ tooltips: false }}>
                {v.slice(0, 8)}…
              </Typography.Text>
            ),
          },
          {
            title: 'Ref',
            width: 180,
            render: (_v, row) => {
              if (row.refOrderId) {
                return (
                  <Tooltip title={`Ordine ${row.refOrderId}`}>
                    <Typography.Text code style={{ fontSize: 11 }}>
                      ORD {row.refOrderId.slice(0, 8)}…
                    </Typography.Text>
                  </Tooltip>
                );
              }
              if (row.refExternalId) {
                return (
                  <Tooltip title={`Scontrino POS ${row.refExternalId}`}>
                    <Typography.Text code style={{ fontSize: 11 }}>
                      POS {row.refExternalId}
                    </Typography.Text>
                  </Tooltip>
                );
              }
              return null;
            },
          },
          {
            title: 'Note',
            dataIndex: 'notes',
            ellipsis: true,
            render: (v: string | null) =>
              v ? (
                <Typography.Text type={v.startsWith('OVERSELL') ? 'danger' : 'secondary'}>
                  {v}
                </Typography.Text>
              ) : null,
          },
        ]}
      />
    </Card>
  );
}
